import { ApolloCache, ApolloClient, DocumentNode, useApolloClient, useReactiveVar } from '@apollo/client';
import { startOfMinute } from 'date-fns';
import { isSameDayTz, startOfDayTz, startOfNextDayTZ } from '@tools/timeUtilities';
import { SEARCH_SCHEDULE_LIMIT } from '@components/mySchedule/constants';
import { MyScheduleFilter, myScheduleFilterVar } from '@components/mySchedule/myScheduleFilter';
import useMeQuery from '@hooks/useMeQuery';
import compareAgendaItems from '@tools/compareAgendaItems';
import usePreferredTimezone from '@hooks/usePreferredTimezone';
import useLocaleForQueries from '@hooks/useLocaleForQueries';
import { MyAgendaCriteria } from '@graphql/gql/graphql';
import useAppFeatures from '@hooks/useAppFeatures';
import { SEARCH_SCHEDULE } from '@graphql/operations/schedule/searchSchedule';

type UpdateAgendaItemsCacheProps = {
  // Current newAgendaItem can be one of many different objects which we use to store in cache.
  // It's type can properly be defined after unifying the return types of the data
  // returned from the mutations CREATE_APPOINTMENT, PROPOSE_NEW_TIME, DELETE_PERSONAL_TIME,
  // UPDATE_PERSONAL_TIME, UPDATE_APPOINTMENT, DECLINE_APPOINTMENT, ACCEPT_APPOINTMENT,
  // and GET_APPOINTMENT_AGENDA_ITEM. This is to be handled in HUB-132694.
  newAgendaItem: any;
  change: string;
  apolloClient: ApolloClient<object>;
  searchScheduleCriteria: MyScheduleFilter;
  eventStart: string;
  eventEnd: string;
  preferredTimezone: string;
  locale: string;
  oldAgendaItemStartTime?: string;
  virtualAttendee?: boolean;
  enableSessionCapacity?: boolean;
  enableSessionFee?: boolean;
};

export const useAgendaItemsCache = (): ((data: {
  // Same as comment in UpdateAgendaItemsCacheProps.
  // TODO: Will be handled in HUB-132694.
  newAgendaItem: any;
  change: string;
  oldAgendaItemStartTime?: string;
}) => void) => {
  const apolloClient = useApolloClient();
  const { data: userData } = useMeQuery();
  const { preferredTimezone } = usePreferredTimezone();
  const locale = useLocaleForQueries();
  const myScheduleFilter = useReactiveVar(myScheduleFilterVar);
  const { sessionCapacity, sessionFee } = useAppFeatures();

  // Same as comment in UpdateAgendaItemsCacheProps.
  // TODO: Will be handled in HUB-132694.
  const updateLocalAgendaItem = (data: { newAgendaItem: any; change: string; oldAgendaItemStartTime: string }) => {
    updateAgendaItemsCache({
      ...data,
      apolloClient,
      searchScheduleCriteria: myScheduleFilter,
      eventStart: userData?.me?.event?.start,
      eventEnd: userData?.me?.event?.end,
      preferredTimezone,
      locale,
      virtualAttendee: userData?.me?.virtual || false,
      enableSessionCapacity: sessionCapacity,
      enableSessionFee: sessionFee
    });
  };
  return updateLocalAgendaItem;
};

const PREFIX_AGENDA_ITEM = 'AgendaItem:';

const getUpdatedAgendaItems = ({ myAgenda, change, newAgendaItem }) => {
  let updatedAgendaItems = [...myAgenda.data];
  switch (change) {
    case 'add':
      updatedAgendaItems.push(newAgendaItem);
      updatedAgendaItems.sort(compareAgendaItems);
      break;

    case 'update':
      updatedAgendaItems = updatedAgendaItems
        .map(item => (newAgendaItem.id === item.id ? newAgendaItem : item))
        .sort(compareAgendaItems);
      break;

    case 'remove':
      updatedAgendaItems = updatedAgendaItems.filter(item => newAgendaItem.id !== item.id);
      break;

    case 'accept': {
      const impactedAgendaItemsSet = new Set();
      newAgendaItem.impactedAgendaItems.forEach(impactedAgendaItem => {
        impactedAgendaItemsSet.add(impactedAgendaItem.id);
      });
      updatedAgendaItems = updatedAgendaItems.filter(item => !impactedAgendaItemsSet.has(item.id));
      const index = updatedAgendaItems.findIndex(agendaItem => newAgendaItem.id === agendaItem.id);
      if (index > -1) {
        updatedAgendaItems[index] = newAgendaItem;
      }
      break;
    }

    default:
      throw new Error(`Invalid change type: ${change} passed`);
  }
  const updatedTotalCount = myAgenda.pagination.totalCount + (updatedAgendaItems.length - myAgenda.data.length);
  if (
    (change === 'add' || change === 'update') &&
    updatedAgendaItems &&
    updatedAgendaItems.length < updatedTotalCount &&
    updatedAgendaItems[updatedAgendaItems.length - 1].id === newAgendaItem.id
  ) {
    updatedAgendaItems.pop();
  }
  return { updatedAgendaItems, updatedTotalCount };
};

const getSearchScheduleQueryOptions = (
  agendaItemStartTime: string,
  locale: string,
  eventStart: string,
  eventEnd: string,
  preferredTimezone: string,
  virtualAttendee: boolean,
  enableSessionCapacity: boolean,
  enableSessionFee: boolean
): {
  query: DocumentNode;
  variables: {
    criteria: MyAgendaCriteria;
    virtualAttendee: boolean;
    enableSessionCapacity: boolean;
    enableSessionFee: boolean;
  };
} => {
  const { start, end } = getDateTabTimeRange(eventStart, eventEnd, agendaItemStartTime, preferredTimezone);
  return {
    query: SEARCH_SCHEDULE,
    variables: {
      criteria: {
        pageCriteria: {
          limit: SEARCH_SCHEDULE_LIMIT.AGENDA_LIST
        },
        locale,
        start: start?.toISOString(),
        end: end?.toISOString()
      },
      virtualAttendee,
      enableSessionCapacity,
      enableSessionFee
    }
  };
};

const updateCache = (
  cache,
  change: string,
  searchScheduleQueryOptions: { query: DocumentNode; variables: { criteria: MyAgendaCriteria } },
  newAgendaItem
): void => {
  const { myAgenda } = cache.readQuery(searchScheduleQueryOptions) || {};

  // TODO HUB-99636
  const updatedNewAgendaItem = {
    ...newAgendaItem,
    contentTags: null
  };

  if (myAgenda) {
    const { updatedAgendaItems, updatedTotalCount } = getUpdatedAgendaItems({
      myAgenda,
      change,
      newAgendaItem: updatedNewAgendaItem
    });

    // update agenda item cache
    cache.writeQuery({
      ...searchScheduleQueryOptions,
      data: {
        myAgenda: {
          ...myAgenda,
          data: updatedAgendaItems,
          pagination: { ...myAgenda.pagination, totalCount: updatedTotalCount }
        }
      },
      overwrite: true
    });
  }
};

/**
 * Basis the event start and end time, get upper and lower bound of the date tab in which the agenda item
 * with given start time will fall
 */
const getDateTabTimeRange = (
  eventStart: string,
  eventEnd: string,
  agendaStart: string,
  preferredTimezone: string
): { start: Date; end: Date } => {
  let start = null;
  let end = null;
  if (startOfDayTz(agendaStart, preferredTimezone) < startOfDayTz(eventStart, preferredTimezone)) {
    // Before event date tab
    end = startOfDayTz(eventStart, preferredTimezone);
  } else if (startOfDayTz(agendaStart, preferredTimezone) > startOfDayTz(eventEnd, preferredTimezone)) {
    // After event date tab
    start = startOfNextDayTZ(eventEnd, preferredTimezone);
  } else {
    // Mid event date tab
    start = startOfDayTz(agendaStart, preferredTimezone);
    end = startOfNextDayTZ(agendaStart, preferredTimezone);
  }
  return { start, end };
};

/**
 * Update the SEARCH_SCHEDULE query cache corresponding to current applied filter with the newly added agenda item if
 * it matches the applied filters
 */
const updateCurrentFilterCache = (
  cache: ApolloCache<object>,
  change: string,
  eventStart: string,
  eventEnd: string,
  locale: string,
  preferredTimezone: string,
  newAgendaItem,
  searchScheduleCriteria: MyScheduleFilter,
  virtualAttendee: boolean,
  enableSessionCapacity: boolean,
  enableSessionFee: boolean
): void => {
  const { start, end } = getDateTabTimeRange(eventStart, eventEnd, newAgendaItem.start, preferredTimezone);

  if (
    (searchScheduleCriteria?.term ||
      (searchScheduleCriteria?.agendaTypes && searchScheduleCriteria?.agendaTypes.length > 0)) &&
    searchScheduleCriteria?.start === start.toISOString() &&
    searchScheduleCriteria?.end === end.toISOString()
  ) {
    const searchScheduleQueryOptions = {
      query: SEARCH_SCHEDULE,
      variables: {
        criteria: {
          pageCriteria: {
            limit: SEARCH_SCHEDULE_LIMIT.UPCOMING
          },
          locale,
          term: searchScheduleCriteria?.term,
          agendaTypes: searchScheduleCriteria?.agendaTypes,
          start: searchScheduleCriteria?.start,
          end: searchScheduleCriteria?.end
        },
        virtualAttendee,
        enableSessionCapacity,
        enableSessionFee
      }
    };
    updateCache(cache, change, searchScheduleQueryOptions, newAgendaItem);
  }
};
const updateUpcomingAgendaCache = (
  cache: ApolloCache<object>,
  change: string,
  locale: string,
  newAgendaItem,
  virtualAttendee: boolean,
  enableSessionCapacity: boolean,
  enableSessionFee: boolean
): void => {
  const searchScheduleQueryOptions = {
    query: SEARCH_SCHEDULE,
    variables: {
      criteria: {
        pageCriteria: {
          limit: SEARCH_SCHEDULE_LIMIT.UPCOMING
        },
        locale,
        start: startOfMinute(Date.now())
      },
      virtualAttendee,
      enableSessionCapacity,
      enableSessionFee
    }
  };
  updateCache(cache, change, searchScheduleQueryOptions, newAgendaItem);
};

const updateAllDaysAgendaCache = (
  cache: ApolloCache<object>,
  change: string,
  locale: string,
  newAgendaItem,
  virtualAttendee: boolean,
  enableSessionCapacity: boolean,
  enableSessionFee: boolean
): void => {
  const searchScheduleQueryOptions = {
    query: SEARCH_SCHEDULE,
    variables: {
      criteria: {
        pageCriteria: {
          limit: SEARCH_SCHEDULE_LIMIT.AGENDA_LIST
        },
        locale
      },
      virtualAttendee,
      enableSessionCapacity,
      enableSessionFee
    }
  };
  updateCache(cache, change, searchScheduleQueryOptions, newAgendaItem);
};

const handleRemoveAgendaItem = (locale: string, removedAgendaItemId: string, cache: ApolloCache<object>): void => {
  const cacheKey = `${PREFIX_AGENDA_ITEM}${removedAgendaItemId}:${locale}`;
  cache.evict({ id: cacheKey });
  cache.gc();
};

const handleCreateAgendaItem = (
  change: string,
  cache: ApolloCache<object>,
  newAgendaItem,
  locale: string,
  eventStart: string,
  eventEnd: string,
  preferredTimezone: string,
  searchScheduleCriteria: MyScheduleFilter,
  virtualAttendee: boolean,
  enableSessionCapacity: boolean,
  enableSessionFee: boolean
): void => {
  // update the SEARCH_SCHEDULE cache where the new appointment will fall
  const searchScheduleQueryOptions = getSearchScheduleQueryOptions(
    newAgendaItem.start,
    locale,
    eventStart,
    eventEnd,
    preferredTimezone,
    virtualAttendee,
    enableSessionCapacity,
    enableSessionFee
  );
  updateCache(cache, change, searchScheduleQueryOptions, newAgendaItem);

  if (searchScheduleCriteria) {
    updateCurrentFilterCache(
      cache,
      change,
      eventStart,
      eventEnd,
      locale,
      preferredTimezone,
      newAgendaItem,
      searchScheduleCriteria,
      virtualAttendee,
      enableSessionCapacity,
      enableSessionFee
    );
  }
};

const handleUpdateAgendaItem = (
  cache: ApolloCache<object>,
  oldAgendaItemStartTime: string,
  newAgendaItem,
  locale: string,
  eventStart: string,
  eventEnd: string,
  preferredTimezone: string,
  virtualAttendee: boolean,
  enableSessionCapacity: boolean,
  enableSessionFee: boolean
): void => {
  if (oldAgendaItemStartTime && !isSameDayTz(oldAgendaItemStartTime, newAgendaItem.start, preferredTimezone)) {
    // if agenda is updated to some other day than, we need to add the agenda in cache for the new date
    // and remove the agenda from cache for the existing date

    // remove the agenda in the cache where old agenda is created
    const removeAgendaItemQueryOptions = getSearchScheduleQueryOptions(
      oldAgendaItemStartTime,
      locale,
      eventStart,
      eventEnd,
      preferredTimezone,
      virtualAttendee,
      enableSessionCapacity,
      enableSessionFee
    );
    updateCache(cache, 'remove', removeAgendaItemQueryOptions, newAgendaItem);

    // add the agenda to the cache where the agenda is created
    const addAgendaItemQueryOptions = getSearchScheduleQueryOptions(
      newAgendaItem.start,
      locale,
      eventStart,
      eventEnd,
      preferredTimezone,
      virtualAttendee,
      enableSessionCapacity,
      enableSessionFee
    );
    updateCache(cache, 'add', addAgendaItemQueryOptions, newAgendaItem);
  }
};

/**
 * Updates various SEARCH_SCHEDULE Apollo Cache when an agenda item is changed
 */
const updateAgendaItemsCache = (data: UpdateAgendaItemsCacheProps): void => {
  const {
    apolloClient,
    newAgendaItem,
    change,
    locale,
    searchScheduleCriteria,
    eventStart,
    eventEnd,
    preferredTimezone,
    oldAgendaItemStartTime,
    virtualAttendee,
    enableSessionCapacity,
    enableSessionFee
  } = data;
  const { cache } = apolloClient;

  if (change === 'remove') {
    handleRemoveAgendaItem(locale, newAgendaItem.id, cache);
    return;
  }

  if (change === 'add') {
    handleCreateAgendaItem(
      change,
      cache,
      newAgendaItem,
      locale,
      eventStart,
      eventEnd,
      preferredTimezone,
      searchScheduleCriteria,
      virtualAttendee,
      enableSessionCapacity,
      enableSessionFee
    );
  }

  if (change === 'update') {
    handleUpdateAgendaItem(
      cache,
      oldAgendaItemStartTime,
      newAgendaItem,
      locale,
      eventStart,
      eventEnd,
      preferredTimezone,
      virtualAttendee,
      enableSessionCapacity,
      enableSessionFee
    );
  }

  // update all days cache
  updateAllDaysAgendaCache(
    cache,
    change,
    locale,
    newAgendaItem,
    virtualAttendee,
    enableSessionCapacity,
    enableSessionFee
  );

  // update cache for upcoming agenda items flyout
  updateUpcomingAgendaCache(
    cache,
    change,
    locale,
    newAgendaItem,
    virtualAttendee,
    enableSessionCapacity,
    enableSessionFee
  );
};
